import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Printer } from 'lucide-react';

interface PDFExportProps {
  data: any;
  title: string;
  reportType: 'overview' | 'detailed';
}

export function PDFExport({ data, title, reportType }: PDFExportProps) {
  const [isOpen, setIsOpen] = useState(false);

  const generatePrintableReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString('cs-CZ');
    const currentTime = new Date().toLocaleTimeString('cs-CZ');

    let htmlContent = `
      <!DOCTYPE html>
      <html lang="cs">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>BLAZE HARMONY - ${title}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 210mm;
            margin: 0 auto;
            padding: 20px;
            background: white;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .logo {
            font-size: 24px;
            font-weight: bold;
            color: #d97706;
            margin-bottom: 10px;
          }
          .report-title {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .report-meta {
            font-size: 12px;
            color: #666;
          }
          .section {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          .section-title {
            font-size: 16px;
            font-weight: bold;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
            margin-bottom: 15px;
          }
          .metric-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
            margin-bottom: 20px;
          }
          .metric-card {
            border: 1px solid #ddd;
            padding: 15px;
            border-radius: 5px;
          }
          .metric-value {
            font-size: 24px;
            font-weight: bold;
            color: #d97706;
          }
          .metric-label {
            font-size: 12px;
            color: #666;
            margin-top: 5px;
          }
          .chart-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .chart-table th,
          .chart-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          .chart-table th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .bar-visual {
            height: 20px;
            background-color: #d97706;
            border-radius: 3px;
            margin: 2px 0;
          }
          .badge {
            display: inline-block;
            padding: 2px 8px;
            background-color: #f0f0f0;
            border-radius: 3px;
            font-size: 11px;
            margin: 2px;
          }
          .badge.success { background-color: #dcfce7; color: #166534; }
          .badge.warning { background-color: #fef3c7; color: #92400e; }
          .badge.error { background-color: #fecaca; color: #991b1b; }
          .badge.info { background-color: #dbeafe; color: #1d4ed8; }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 12px;
            color: #666;
            text-align: center;
          }
          @media print {
            body { margin: 0; padding: 15mm; }
            .no-print { display: none; }
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">BLAZE HARMONY</div>
          <div class="report-title">${title}</div>
          <div class="report-meta">
            Vygenerováno: ${currentDate} ${currentTime}
          </div>
        </div>
    `;

    if (reportType === 'overview' && data) {
      // Overview Report
      htmlContent += `
        <div class="section">
          <div class="section-title">Klíčové metriky</div>
          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-value">${data.overview?.totalSubmissions || 0}</div>
              <div class="metric-label">Celkem formulářů</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${data.overview?.conversionRate?.total > 0 
                ? ((data.overview.conversionRate.completed / data.overview.conversionRate.total) * 100).toFixed(1)
                : 0}%</div>
              <div class="metric-label">Míra konverze</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${data.overview?.avgResponseTime ? data.overview.avgResponseTime.toFixed(1) : 0} dní</div>
              <div class="metric-label">Průměrná doba vyřízení</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${data.representativePerformance?.length || 0}</div>
              <div class="metric-label">Aktivní obchodníci</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Rozložení stavů formulářů</div>
          <table class="chart-table">
            <thead>
              <tr>
                <th>Stav</th>
                <th>Počet</th>
                <th>Procenta</th>
                <th>Vizualizace</th>
              </tr>
            </thead>
            <tbody>
      `;

      data.statusDistribution?.forEach((item: any) => {
        const statusMap: { [key: string]: string } = {
          'draft': 'Koncept',
          'submitted': 'Odeslaný',
          'synced': 'Synchronizován',
          'error': 'Chyba'
        };
        htmlContent += `
          <tr>
            <td>${statusMap[item.status] || item.status}</td>
            <td>${item.count}</td>
            <td>${item.percentage.toFixed(1)}%</td>
            <td>
              <div class="bar-visual" style="width: ${item.percentage}%"></div>
            </td>
          </tr>
        `;
      });

      htmlContent += `
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">Výkon obchodníků</div>
          <table class="chart-table">
            <thead>
              <tr>
                <th>Obchodník</th>
                <th>Počet formulářů</th>
                <th>Vizualizace</th>
              </tr>
            </thead>
            <tbody>
      `;

      const maxRep = Math.max(...(data.representativePerformance?.map((rep: any) => rep.count) || [1]));
      data.representativePerformance?.forEach((rep: any) => {
        htmlContent += `
          <tr>
            <td>${rep.representative}</td>
            <td>${rep.count}</td>
            <td>
              <div class="bar-visual" style="width: ${(rep.count / maxRep) * 100}%"></div>
            </td>
          </tr>
        `;
      });

      htmlContent += `
            </tbody>
          </table>
        </div>

        <div class="section page-break">
          <div class="section-title">Tržní poznatky</div>
          
          <h4>Dojmy z designu</h4>
          <table class="chart-table">
            <thead>
              <tr><th>Dojem</th><th>Počet</th></tr>
            </thead>
            <tbody>
      `;

      data.marketInsights?.designImpressions?.forEach((item: any) => {
        const impressionMap: { [key: string]: string } = {
          'very-positive': 'Velmi pozitivní',
          'rather-positive': 'Spíše pozitivní',
          'neutral': 'Neutrální',
          'rather-negative': 'Spíše negativní',
          'very-negative': 'Velmi negativní'
        };
        htmlContent += `
          <tr>
            <td>${impressionMap[item.impression] || item.impression}</td>
            <td>${item.count}</td>
          </tr>
        `;
      });

      htmlContent += `
            </tbody>
          </table>

          <h4>Top konkurenti</h4>
          <table class="chart-table">
            <thead>
              <tr><th>Konkurent</th><th>Zmínky</th></tr>
            </thead>
            <tbody>
      `;

      data.marketInsights?.topCompetitors?.forEach((item: any) => {
        htmlContent += `
          <tr>
            <td>${item.competitor}</td>
            <td>${item.mentions}</td>
          </tr>
        `;
      });

      htmlContent += `
            </tbody>
          </table>
        </div>
      `;
    }

    htmlContent += `
        <div class="footer">
          <p>Tento report byl vygenerován systémem BLAZE HARMONY</p>
          <p>Všechna data jsou aktuální k datu vytvoření reportu</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handlePDFExport = () => {
    generatePrintableReport();
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <FileText className="h-4 w-4 mr-2" />
          Export PDF
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export do PDF</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informace o reportu</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <strong>Název:</strong> {title}
                </div>
                <div>
                  <strong>Typ:</strong> {reportType === 'overview' ? 'Přehledový report' : 'Detailní analýza'}
                </div>
                <div>
                  <strong>Datum vytvoření:</strong> {new Date().toLocaleDateString('cs-CZ')}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="bg-orange-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Instrukce pro export:</h4>
            <ol className="text-sm space-y-1 list-decimal list-inside">
              <li>Klikněte na tlačítko "Generovat PDF Report"</li>
              <li>Otevře se nové okno s print-friendly verzí reportu</li>
              <li>Automaticky se otevře dialog pro tisk</li>
              <li>Vyberte "Uložit jako PDF" nebo tiskněte na PDF tiskárnu</li>
              <li>Uložte soubor na požadované místo</li>
            </ol>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Zrušit
            </Button>
            <Button onClick={handlePDFExport}>
              <Printer className="h-4 w-4 mr-2" />
              Generovat PDF Report
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}